import { Box, Button, Dialog, makeStyles } from "@material-ui/core";
import { useEffect, useState } from "react";
import useKitApi, { KitPackage as KitPackageRepresentation, PackagingType } from "../../api/KitApi";
import KitPackageDefinition from "./KitPackageDefinition";
import KitPackageName from "./KitPackageName";
import { PackageElement, Quantifiable } from "./KitPackageElement";

interface KitPackage {
  id?: number,
  name: string;
  packagingType: PackagingType;
  forms: Quantifiable[];
  shapes: Quantifiable[];
}

interface Properties {
  open: boolean;
  customerId: number;
  priceListId: number;
  kitId: number;
  onDialogClosed: () => void;
  reportAlert: (message: string) => void;
};

type PackageModifierCreator = (packageElement: PackageElement, quantity: number)
  => (p: KitPackage) => KitPackage;

const modifyQuantity = (values: Quantifiable[], quantifiable: Quantifiable, quantity: number) => {
  const findById = (value: Quantifiable) => value.id === quantifiable.id;
  const result = values.find(findById) === undefined
    ? values.concat({ ...quantifiable, quantity })
    : values.map(value => value.id === quantifiable.id ? ({ ...value, quantity }) : value);
  return result;
};

const createPackageFormsModifier = (packageElement: PackageElement, quantity: number) =>
  (p: KitPackage): KitPackage => ({ ...p, forms: modifyQuantity(p.forms, packageElement, quantity) });

const createPackageShapesModifier = (packageElement: PackageElement, quantity: number) =>
  (p: KitPackage): KitPackage => ({ ...p, shapes: modifyQuantity(p.shapes, packageElement, quantity) });

const useStyles = makeStyles(theme => ({
  button: {
    marginBottom: theme.spacing(1),
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1)
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(3)
  }
}));

const mapMaterialHoldingItemToPackageElement = (item: any, limit: number) => ({
  id: item.id,
  name: `${item.name} (${item.material.name})`,
  quantity: 0,
  limit
});

const mapKitFormToPackageElement = (kitForm: any) => mapMaterialHoldingItemToPackageElement(kitForm.form, kitForm.quantity);

const mapKitShapeToPackageElement = (kitShape: any) => mapMaterialHoldingItemToPackageElement(kitShape.shape, kitShape.quantity);

const transformToKitPackageRepresentation = (kitPackage: KitPackage) => ({
  name: kitPackage.name,
  packagingType: kitPackage.packagingType,
  packageForms: kitPackage.forms.map(mapToKitPackageFormRepresentation),
  packageShapes: kitPackage.shapes.map(mapToKitPackageShapeRepresentation)
});

const mapToKitPackageFormRepresentation = (packageForm: Quantifiable) => ({
  form: {
    id: packageForm.id
  },
  quantity: packageForm.quantity
});

const mapToKitPackageShapeRepresentation = (packageShape: Quantifiable) => ({
  shape: {
    id: packageShape.id
  },
  quantity: packageShape.quantity
});

const findKitForm = (kitPackage: KitPackage, formId: number) => kitPackage.forms.find(form => form.id === formId);

const findKitShape = (kitPackage: KitPackage, shapeId: number) => kitPackage.shapes.find(shape => shape.id === shapeId);

const KitPackagesDialog = (properties: Properties) => {
  const classes = useStyles();
  const kitApi = useKitApi();
  const [kitPackages, setKitPackages] = useState<KitPackage[]>([]);
  const [kitForms, setKitForms] = useState<PackageElement[]>([]);
  const [kitShapes, setKitShapes] = useState<PackageElement[]>([]);

  const handlePackageNameConfirmed = (packageName: string) => {
    setKitPackages(kitPackages.concat({
      name: packageName,
      packagingType: 'none',
      forms: [],
      shapes: []
    }));
  };

  const isKitElementFullyPacked = (
    packageElement: PackageElement,
    packageElementsAccessor: (kitPackage: KitPackage) => Quantifiable[]
  ) => {
    const kitElementsPackedTotal = kitPackages
      .flatMap(kitPackage => packageElementsAccessor(kitPackage).filter(form => form.id === packageElement.id))
      .map(packageElement => packageElement.quantity)
      .reduce((totalQuantity, packageElementQuantity) => totalQuantity + packageElementQuantity, 0);
    return kitElementsPackedTotal == packageElement.limit;
  };

  const findInvalidKitPackageElement = () => {
    const invalidKitForm = kitForms.find(kitForm => isKitElementFullyPacked(kitForm, kitPackage => kitPackage.forms) === false);
    return invalidKitForm === undefined
      ? kitShapes.find(kitShape => isKitElementFullyPacked(kitShape, kitPackage => kitPackage.shapes) === false)
      : invalidKitForm;
  };

  const handleSaveKitPackagesClicked = () => {
    const invalidKitPackageElement = findInvalidKitPackageElement();
    if (invalidKitPackageElement === undefined) {
      kitApi.saveKitPackages(properties.customerId, properties.kitId, kitPackages.map(transformToKitPackageRepresentation));
      properties.onDialogClosed();
    } else {
      properties.reportAlert(`Specyfikacja pakowania nieprawidłowa: ${invalidKitPackageElement.name}`);
    }
  };

  const handleCancelClicked = () => {
    properties.onDialogClosed();
  };

  const setKitFormsMappedToPackageElements = (kitForms: any[]) => setKitForms(kitForms.map(mapKitFormToPackageElement));

  const setKitShapesMappedToPackageElements = (kitShapes: any[]) => setKitShapes(kitShapes.map(mapKitShapeToPackageElement));

  const getPackageFormQuantity = (formId: number) => kitForms.find(kitForm => kitForm.id === formId)?.limit || 0;

  const getPackageShapeQuantity = (shapeId: number) => kitShapes.find(kitShape => kitShape.id === shapeId)?.limit || 0;

  const mapFromKitPackageRepresentation = (kitPackage: KitPackageRepresentation) => ({
    id: kitPackage.id,
    name: kitPackage.name,
    packagingType: kitPackage.packagingType,
    forms: kitPackage.packageForms ? kitPackage.packageForms
      .map(packageForm => ({ id: packageForm.form.id, quantity: packageForm.quantity, limit: getPackageFormQuantity(packageForm.form.id) })) : [],
    shapes: kitPackage.packageShapes ? kitPackage.packageShapes
      .map(packageShape => ({ id: packageShape.shape.id, quantity: packageShape.quantity, limit: getPackageShapeQuantity(packageShape.shape.id) })) : []
  });

  const setKitPackagesMappedFromRepresentation = (kitPackages: KitPackageRepresentation[]) =>
    setKitPackages(kitPackages.map(mapFromKitPackageRepresentation));
  
  const fetchKitElementsAndPackages = () => {
    const customerId = properties.customerId;
    const kitId = properties.kitId;
    const priceListId = properties.priceListId;
    kitApi
      .fetchKitForms(customerId, priceListId, kitId)
      .then(setKitFormsMappedToPackageElements)
      .then(() => kitApi.fetchKitShapes(customerId, priceListId, kitId))
      .then(setKitShapesMappedToPackageElements)
      .then(() => kitApi.fetchKitPackages(customerId, kitId))
      .then(setKitPackagesMappedFromRepresentation)
  }

  useEffect(() => {
    if (properties.open === true) {
      fetchKitElementsAndPackages();
    }
  }, [properties.open]);


  const calculateKitElementsPackedQuantityInOtherPackages = (
    packageElementId: number,
    kitPackage: KitPackage,
    packageElementAccessor: (kitPackage: KitPackage) => Quantifiable[]
  ) => kitPackages
        .filter(otherKitPackage => otherKitPackage !== kitPackage)
        .flatMap(packageElementAccessor)
        .filter(packageElement => packageElement.id === packageElementId)
        .map(packageElement => packageElement.quantity || 0)
        .reduce((totalQuantity, packageElementQuantity) => totalQuantity + packageElementQuantity, 0);

  const calculateKitFormPackedQuantityInOtherPackages = (packageElementId: number, kitPackage: KitPackage) =>
    calculateKitElementsPackedQuantityInOtherPackages(packageElementId, kitPackage, otherKitPackage => otherKitPackage.forms);

  const calculateKitShapesPackedQuantityInOtherPackages = (packageElementId: number, kitPackage: KitPackage) =>
    calculateKitElementsPackedQuantityInOtherPackages(packageElementId, kitPackage, otherKitPackage => otherKitPackage.shapes);

  const getKitFormsNotTotallyPackedToOtherPackages = (kitPackage: KitPackage, selectedPackageForms: Quantifiable[]) => {
    const selectedFormIds = selectedPackageForms.map(kitForm => kitForm.id);
    return kitForms
      .map(kitForm => ({ ...kitForm, quantity: calculateKitFormPackedQuantityInOtherPackages(kitForm.id, kitPackage) }))
      .map(kitForm => {
        console.info(`### ${kitPackage.name}: ${JSON.stringify(kitForm)}: ${kitForm.quantity < kitForm.limit || selectedFormIds.includes(kitForm.id)}`);
        return kitForm;
      })
      .filter(kitForm => kitForm.quantity < kitForm.limit || selectedFormIds.includes(kitForm.id));
  };
    
  const getKitShapesNotTotallyPackedToOtherPackages = (kitPackage: KitPackage, selectedPackageShapes: Quantifiable[]) => {
    const selectedShapeIds = selectedPackageShapes.map(kitShape => kitShape.id);
    return kitShapes
      .map(kitShape => ({ ...kitShape, quantity: calculateKitShapesPackedQuantityInOtherPackages(kitShape.id, kitPackage) }))
      .filter(kitShape => kitShape.quantity < kitShape.limit || selectedShapeIds.includes(kitShape.id));
  };

  const getSelectedKitForms = (kitPackage: KitPackage) =>
    kitForms
      .map(kitForm => ({ ...kitForm, quantity: findKitForm(kitPackage, kitForm.id)?.quantity || 0 }))
      .filter(kitForm => kitForm.quantity !== 0);

  const getSelectedKitShapes = (kitPackage: KitPackage) =>
    kitShapes
      .map(kitShape => ({ ...kitShape, quantity: findKitShape(kitPackage, kitShape.id)?.quantity || 0 }))
      .filter(kitShape => kitShape.quantity !== 0);

  const handlePackageElementSelectionChanged = (kitPackage: KitPackage, createPackageModifier: PackageModifierCreator) =>
    (packageElement: PackageElement, quantity: number) => {
      console.info(`## KitPackagesDialog.handlePackageElementSelectionChanged: ${kitPackage.name}: ${packageElement.name} / ${quantity}}`);
      const getModifiedPackage = createPackageModifier(packageElement, quantity);
      setKitPackages(kitPackages.map(p => p === kitPackage ? getModifiedPackage(p) : p));
    };

  const handleFormPackageElementSelectionChanged = (kitPackage: KitPackage) =>
    handlePackageElementSelectionChanged(kitPackage, createPackageFormsModifier);

  const handleShapePackageElementSelectionChanged = (kitPackage: KitPackage) =>
    handlePackageElementSelectionChanged(kitPackage, createPackageShapesModifier);

  const handleKitPackageDeleted = (kitPackage: KitPackage) =>
    () => setKitPackages(kitPackages.filter(p => p !== kitPackage));

  const handleKitPackageNameChanged = (kitPackage: KitPackage) =>
    (kitPackageName: string) => {
      const kitPackageId = kitPackage.id;
      if (kitPackageId === undefined) {
        console.debug('# handleKitPackageNameChanged no kitPackage.id given'); 
      } else {
        setKitPackages(kitPackages.map(p => p.id === kitPackageId ? { ...p, name: kitPackageName } : p));
        kitApi.changeKitPackageName(properties.customerId, properties.kitId, kitPackageId, kitPackageName);
      }
    };

  const handlePackagingTypeSelected = (kitPackage: KitPackage) => (packagingType: PackagingType) =>
    setKitPackages(kitPackages.map(p => p === kitPackage ? { ...p, packagingType } : p));

  const renderKitPackage = (kitPackage: KitPackage) => {
    const selectedKitForms = getSelectedKitForms(kitPackage);
    const selectedKitShapes = getSelectedKitShapes(kitPackage);
    return <KitPackageDefinition
      kitPackageName={ kitPackage.name }
      availableForms={ getKitFormsNotTotallyPackedToOtherPackages(kitPackage, selectedKitForms) }
      availableShapes={ getKitShapesNotTotallyPackedToOtherPackages(kitPackage, selectedKitShapes) }
      selectedForms={ selectedKitForms }
      selectedShapes={ selectedKitShapes }
      packagingType={ kitPackage.packagingType }
      onKitPackageDeleted={ handleKitPackageDeleted(kitPackage) }
      onKitPackageNameChanged={ handleKitPackageNameChanged(kitPackage) }
      onFormPackageElementQuantityChanged={ handleFormPackageElementSelectionChanged(kitPackage) }
      onShapePackageElementQuantityChanged={ handleShapePackageElementSelectionChanged(kitPackage) }
      onPackagingTypeSelected={ handlePackagingTypeSelected(kitPackage) }
    />;
  }

  return (
    <Dialog open={ properties.open } maxWidth={ false }>
      <Box className={ classes.dialogContent }>
        <h2>Specyfikacja pakowania</h2>
        <KitPackageName
          existingPackageNames={ kitPackages.map(kitPackage => kitPackage.name) }
          onPackageNameConfirmed={ handlePackageNameConfirmed }/>
        { kitPackages
            .sort((kitPackage, anotherKitPackage) => kitPackage.name.localeCompare(anotherKitPackage.name))
            .map(renderKitPackage) }
      </Box>
      <Button
        color='primary'
        className={classes.button}
        onClick={() => handleSaveKitPackagesClicked()}
        variant='contained'
      >
        Zapisz
      </Button>
      <Button
        color='secondary'
        className={classes.button}
        onClick={() => handleCancelClicked()}
        variant='contained'
      >
        Anuluj
      </Button>
    </Dialog>)
}

export default KitPackagesDialog;